// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Override this component to implement:
 * - Lazy loading feature: Images will be loadedwhen they are visible in viewports
 * - Responisve images using picture element
 */
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp, injectCss, resolveColor } from "@insite/mobius/utilities";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import Picture from "@pacific/App/Components/Common/Picture";
import { getSettingsCollection } from "@pacific/App/Store/Selectors/SettingsSelectors";
import { getCloudinaryUrl, TransformationsOptions } from "@pacific/XDL/Utilities/cloudinary";
import qs from "qs";
import React from "react";
import { connect } from "react-redux";
import styled, { withTheme } from "styled-components";

export const mapStateToProps = (state: ApplicationState) => ({
    notFoundImagePath:
        getSettingsCollection(state).customSettings?.notFoundLargeImagePath ||
        getSettingsCollection(state).customSettings?.notFoundMediumImagePath ||
        getSettingsCollection(state).customSettings?.notFoundSmallImagePath,
    cloudinaryBaseUrl: getSettingsCollection(state).customSettings?.cloudinaryBaseUrl,
});

export interface LazyImagePresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<LazyImageProps>;
    /** Optional content to show while image is loading.
     * @themable */
    placeholder?: React.ReactNode;
    /** The height of the image.
     * @themable */
    height?: string;
    /** The width of the image.
     * @themable */
    width?: string;
    /** Props passed to the icon component that displays when the image fails to load.
     * @themable */
    errorIconProps?: IconPresentationProps;
    /** Props passed to the typography component that displays when the image fails to load.
     * @themable */
    errorTypographyProps?: TypographyPresentationProps;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
}

export type LazyImageProps = MobiusStyledComponentProps<
    "div",
    LazyImagePresentationProps & {
        /** Props to be passed into the inner `img` element. */
        imgProps?: JSX.IntrinsicElements["img"];
        /** The URL to fetch the image from. If not provided, LazyImage renders `null`. */
        src?: string;
        /** The alternative text to display in place of the image. */
        altText?: string;
        /** Callback function that will be called when the image has finished loading. */
        onLoad?: (newImagePath?: string) => void;
        /** Callback function that will be called if an error occurs when loading an image. */
        onError?: () => void;
        /** Allows to detect pdf generation */
        isShareEntity?: boolean;
        /** <xng-change> */
        /** Props to be passed into the inner `picture` element. */
        pictureProps?: JSX.IntrinsicElements["picture"];
        /** The URL to fetch the image from for small viewports */
        smallImageSrc?: string;
        /** The URL to fetch the image from for medium viewports */
        mediumImageSrc?: string;
        /** The URL to fetch the image from for large viewports */
        largeImageSrc?: string;
        /** use This flag to disable image lazyloading */
        disableLazyLoad?: boolean;
        transformations?: TransformationsOptions;
        urlOptions?: string;
        /** </xng-change> */
    }
>;

// if the image loads in under this number of milliseconds, it doesn't fade in.
const fadeInThreshold = 100;

type State = {
    src?: string;
    loaded: boolean;
    error: boolean;
    startTime: number;
    imageShouldFade: boolean;
    showPlaceholder: boolean;
};

export const LazyImageStyle = styled.div<
    Pick<State, "error" | "imageShouldFade" | "loaded"> & { isShareEntity?: boolean }
>`
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: ${({ error, theme }) => (error ? resolveColor("common.accent", theme) : null)};
    ${({ error }) => error && "padding: 5px;"}
    width: ${getProp("width")};
    height: ${getProp("height")};
    overflow: hidden;
    img {
        width: 100%;
        height: auto;
        max-height: 100%;
        max-width: 100%;
        will-change: opacity;
        transition: opacity ${({ imageShouldFade }) => (imageShouldFade ? ".2s ease" : "0s")};
        ${({ loaded, isShareEntity }) => (isShareEntity ? "" : loaded ? "opacity: 1;" : "opacity: 0;")}
        ${({ error }) => error && "padding: 3px;"}
    }
    .LazyImage-Placeholder {
        width: 100%;
        height: 100%;
        position: absolute;
        will-change: opacity;
        transition: opacity ${({ imageShouldFade }) => (imageShouldFade ? ".2s ease" : "0s")};
        ${({ loaded, isShareEntity }) => (isShareEntity ? "" : loaded ? "opacity: 0;" : "opacity: 1;")}
    }
    p {
        text-align: center;
        display: inline-block;
        padding: 3px;
        width: 100%;
        overflow-wrap: break-word;
        word-wrap: break-word;
        min-height: 0;
    }
    @media print {
        display: block;
    }
    .LazyImage-ErrorMessage {
        overflow: hidden;
    }
    ${injectCss}
`;

export type Props = LazyImageProps & ReturnType<typeof mapStateToProps>;

/**
 * Defers loading of an image until after its first render. If the asset takes over 100ms to load, the image is faded
 * into view.
 */
export class LazyImage extends React.Component<Props, State> {
    imageRef: React.RefObject<HTMLImageElement>;
    _mounted = false;

    constructor(props: Props) {
        super(props);
        this.imageRef = React.createRef<HTMLImageElement>();

        const queryString = props.src && /\?.*/.exec(props.src);

        if (queryString) {
            const obj = qs.parse(queryString[0], { ignoreQueryPrefix: true });

            this.width = props.transformations?.width || obj.width;
            this.height = obj.height;
        } else {
            this.width = props.transformations?.width || "";
            this.height = "";
        }
    }

    image?: HTMLImageElement;
    fadeInTimeout?: number;
    width?: number | string;
    height?: number | string;

    state: State = {
        loaded: false,
        error: false,
        startTime: 0,
        imageShouldFade: false,
        showPlaceholder: false,
    };

    /** <xng-change> */
    onLoadHandler = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
        event.stopPropagation();
        this.setState(
            currentState => ({
                // fade if image takes more than 100ms to load (i.e. not cached)
                imageShouldFade: Date.now() - currentState.startTime > fadeInThreshold,
                loaded: true,
                src: this.props.src,
            }),
            () => {
                this.props.onLoad?.(this.imageRef.current?.currentSrc);
            },
        );
    };

    onErrorHandler = () => {
        this.setState(
            {
                loaded: true,
                error: true,
            },
            this.props.onError,
        );
    };
    /** </xng-change> */

    reloadImage() {
        this.setState({ startTime: Date.now(), loaded: false, error: false });
        this.imageRef.current?.classList.add("lazyload");
        this.fadeInTimeout = setTimeout(() => {
            this.setState({ showPlaceholder: true });
        }, fadeInThreshold);
    }

    componentDidMount() {
        this.reloadImage();
        this._mounted = true;
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.src !== prevProps.src) {
            this.reloadImage();
        }
    }

    componentWillUnmount() {
        this._mounted = false;
        clearTimeout(this.fadeInTimeout);
    }

    /** <xng-change> */
    hasInvalidProps() {
        return !this.props.src && !this.props.notFoundImagePath;
    }

    getImgUrl = (url?: string, width?: number) => {
        let { src: propSrc } = this.props;
        const { notFoundImagePath } = this.props;
        const { error, loaded, src: stateSrc } = this.state;
        // propSrc = src || propSrc;
        propSrc = loaded && !error ? stateSrc : propSrc;
        propSrc = propSrc || notFoundImagePath;
        return getCloudinaryUrl(url || propSrc, this.props.cloudinaryBaseUrl, {
            ...this.props.transformations,
            width: width || this.width,
        }, this.props.urlOptions);
    };

    renderImage(src?: string, fromPic?: boolean) {
        const { imgProps, altText, notFoundImagePath } = this.props;
        const transformedUrl = this.getImgUrl();
        return (
            <img
                ref={this.imageRef}
                alt={altText}
                className="lazyload"
                width={this.props.transformations?.width || this.width}
                height={this.height}
                onLoad={this.onLoadHandler}
                onError={({ currentTarget }) => {
                    if (!notFoundImagePath || currentTarget.src.lastIndexOf(notFoundImagePath) > -1) {
                        this.onErrorHandler();
                        return;
                    }
                    currentTarget.onerror = null;
                    currentTarget.src = notFoundImagePath;
                }}
                data-src={transformedUrl}
                {...imgProps}
            />
        );
    }
    /** </xng-change> */

    render() {
        const {
            mergeCss,
            src: propSrc,
            placeholder,
            imgProps,
            altText,
            disableLazyLoad,
            isShareEntity,
            notFoundImagePath,
            ...otherProps
        } = this.props;
        const { error, imageShouldFade, loaded, showPlaceholder } = this.state;
        const { applyStyledProp, spreadProps } = applyPropBuilder(this.props, { component: "lazyImage" });
        const resolvedMergeCss = mergeCss ?? this.props?.theme?.lazyImage?.defaultProps?.mergeCss;
        /** <xng-change> */
        if (this.hasInvalidProps()) {
            return null;
        }
        /** </xng-change> */

        if (disableLazyLoad || isShareEntity) {
            return (
                <LazyImageStyle css={applyStyledProp("css", resolvedMergeCss)} {...{ loaded: true }} {...otherProps}>
                    <img alt={altText} className="lazyloaded" src={propSrc} {...imgProps} />
                </LazyImageStyle>
            );
        }
        if (this.props.transformations?.tabletWidth || this.props.transformations?.mobileWidth) {
            const mobileUrl = this.getImgUrl("", this.props.transformations?.mobileWidth);
            const tabletUrl = this.getImgUrl("", this.props.transformations?.tabletWidth);
            return (
                <Picture {...otherProps} smallPath={mobileUrl} mediumPath={tabletUrl}>
                    {this.renderImage()}
                </Picture>
            );
        }
        return (
            <LazyImageStyle
                css={applyStyledProp("css", resolvedMergeCss)}
                {...{ error, imageShouldFade, loaded, isShareEntity }}
                {...otherProps}
            >
                {showPlaceholder && <span className="LazyImage-Placeholder">{placeholder}</span>}
                {loaded && error && <IconMemo {...spreadProps("errorIconProps")} title={altText} />}
                {!error && /** <xng-change> */ this._mounted && this.renderImage() /** </xng-change> */}
                {error && (
                    <Typography className="LazyImage-ErrorMessage" {...spreadProps("errorTypographyProps")} as="p">
                        {altText}
                    </Typography>
                )}
            </LazyImageStyle>
        );
    }

    static defaultProps = {
        height: "100%",
        width: "100%",
    };
}

/** @component */
export default withTheme(connect(mapStateToProps)(LazyImage));
